import React, { useEffect, useState, useCallback } from "react";
import { UserPlus, Plus, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

export default function TeamAdmin() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "admin" });
  const load = useCallback(() => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/users", form); toast.success("Admin created"); setForm({ email: "", name: "", password: "", role: "admin" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const approve = async (id) => { await api.post(`/admin/users/${id}/approve`); toast.success("Account approved"); load(); };
  const setRole = async (id, role) => { await api.patch(`/admin/users/${id}`, { role }); load(); };
  const del = async (id) => { try { await api.delete(`/admin/users/${id}`); toast.success("Removed"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={create} className="card-lux p-6 h-fit space-y-3" data-testid="add-admin-form">
        <h3 className="font-serif text-xl text-forest mb-1 flex items-center gap-2"><UserPlus size={18} className="text-rose" /> Add admin user</h3>
        <p className="text-xs text-stone-500 mb-2">Admins manage bookings. Facilities admins manage cleaning, staff, inventory & payroll. System admins have full access.</p>
        <input required type="email" placeholder="Email" value={form.email} data-testid="new-admin-email" onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
        <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
        <input required type="password" placeholder="Temporary password" value={form.password} data-testid="new-admin-password" onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
        <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-admin-role" className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm">
          <option value="admin">Admin (bookings only)</option>
          <option value="facilities_admin">Facilities Admin (cleaning & housekeeping)</option>
          <option value="system_admin">System Admin (full access)</option>
        </select>
        <button className="btn-primary w-full" data-testid="add-admin-submit"><Plus size={15} /> Create user</button>
      </form>
      <div className="lg:col-span-2 space-y-3">
        {users.map((u) => (
          <div key={u.id} className="card-lux p-4 flex items-center justify-between" data-testid={`user-row-${u.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-forest">{u.name || u.email}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${u.role === "system_admin" ? "bg-rose/15 text-rose" : u.role === "admin" ? "bg-blue-100 text-blue-700" : u.role === "facilities_admin" ? "bg-teal-100 text-teal-700" : "bg-stone-100 text-stone-500"}`}>{u.role}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${u.status === "active" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{u.status}</span>
              </div>
              <div className="text-xs text-stone-400">{u.email}</div>
            </div>
            <div className="flex items-center gap-2">
              {u.protected ? (
                <span className="flex items-center gap-1 text-[11px] text-stone-500 px-2 py-1.5 rounded-full bg-stone-100" data-testid={`user-protected-${u.id}`}><Lock size={12} /> Protected</span>
              ) : (
                <>
                  {u.status !== "active" && <button onClick={() => approve(u.id)} data-testid={`approve-user-${u.id}`} className="text-xs px-3 py-1.5 rounded-full bg-green-50 text-green-700">Approve</button>}
                  <select value={u.role} onChange={(e) => setRole(u.id, e.target.value)} className="text-xs rounded-lg border border-stone-200 px-2 py-1.5">
                    <option value="admin">admin</option><option value="facilities_admin">facilities_admin</option><option value="system_admin">system_admin</option><option value="guest">guest</option>
                  </select>
                  <button onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
                </>
              )}
            </div>
          </div>
        ))}
        {users.length === 0 && <p className="text-sm text-stone-400">No admin users yet.</p>}
      </div>
    </div>
  );
}
